/**
 * Monitoring page — sandbox operation statistics dashboard.
 * Renders stat cards, charts, and failure details.
 * Uses 3 independent API calls for incremental loading.
 *
 * @module pages/MonitoringPage
 */

import { useState, useCallback, useMemo } from 'react';
import {
  RefreshCw,
  CheckCircle,
  XCircle,
  Activity,
  Timer,
  TrendingUp,
  AlertTriangle,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardHeader } from '@/components/ui/Card';
import { StatCard } from '@/components/ui/StatCard';
import { TimeRangePicker, getDefaultTimeRange } from '@/components/ui/TimeRangePicker';
import type { TimeRange } from '@/components/ui/TimeRangePicker';
import {
  useMonitoringSummary,
  useMonitoringTimeSeries,
  useMonitoringFailureDetails,
  useInvalidateMonitoring,
} from '@/hooks/useMonitoring';
import { useDarkMode } from '@/hooks';
import type { FailureByErrorCodeTimePoint } from '@/types';

const PIE_COLORS = [
  '#3b82f6',
  '#ef4444',
  '#f97316',
  '#8b5cf6',
  '#22c55e',
  '#06b6d4',
  '#ec4899',
  '#eab308',
  '#6366f1',
  '#14b8a6',
];

function formatNumber(n: number): string {
  return n.toLocaleString('en-US');
}

function formatTimestamp(label: unknown): string {
  const d = new Date(String(label));
  return (
    d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC',
    }) + ' UTC'
  );
}

function formatShortTime(ts: string): string {
  const d = new Date(ts);
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  const time = d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
  return `${month}/${day} ${time}`;
}

function getSuccessRateStatus(rate: number): 'success' | 'warning' | 'danger' {
  if (rate >= 99.9) return 'success';
  if (rate >= 90) return 'warning';
  return 'danger';
}

/** Pivots flat error-code time points into rows suitable for a stacked area chart. */
function pivotErrorCodeTimeSeries(points: FailureByErrorCodeTimePoint[]): {
  data: Record<string, unknown>[];
  errorCodes: string[];
} {
  const codesSet = new Set<string>();
  const byTimestamp = new Map<string, Record<string, unknown>>();

  for (const p of points) {
    codesSet.add(p.errorCode);
    let row = byTimestamp.get(p.timestamp);
    if (!row) {
      row = { timestamp: p.timestamp };
      byTimestamp.set(p.timestamp, row);
    }
    row[p.errorCode] = ((row[p.errorCode] as number) ?? 0) + p.count;
  }

  const errorCodes = [...codesSet];
  const data = [...byTimestamp.values()].sort(
    (a, b) => new Date(a.timestamp as string).getTime() - new Date(b.timestamp as string).getTime()
  );
  return { data, errorCodes };
}

function SectionError({ message }: { message: string }) {
  return (
    <div className="flex items-center gap-2 p-4 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/20 rounded-lg border border-red-200 dark:border-red-900/50">
      <AlertTriangle className="h-4 w-4 flex-shrink-0" />
      {message}
    </div>
  );
}

function ChartSkeleton() {
  return (
    <div className="animate-pulse flex items-end gap-1 h-48 px-4 pb-4">
      {Array.from({ length: 20 }, (_, i) => (
        <div
          key={i}
          className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-t"
          style={{ height: `${20 + Math.random() * 80}%` }}
        />
      ))}
    </div>
  );
}

export function MonitoringPage() {
  const [timeRange, setTimeRange] = useState<TimeRange>(getDefaultTimeRange);
  const [isDark] = useDarkMode();
  const invalidateAll = useInvalidateMonitoring();

  const { startTime, endTime } = timeRange;

  const summary = useMonitoringSummary(startTime, endTime);
  const timeSeries = useMonitoringTimeSeries(startTime, endTime);
  const failures = useMonitoringFailureDetails(startTime, endTime);

  const handleRefresh = useCallback(() => {
    invalidateAll();
  }, [invalidateAll]);

  // Chart theme colors
  const gridColor = isDark ? '#1f1f1f' : '#f0f0f0';
  const textColor = isDark ? '#9ca3af' : '#6b7280';
  const tooltipBg = isDark ? '#1a1a1a' : '#ffffff';
  const tooltipBorder = isDark ? '#2a2a2a' : '#e5e7eb';

  // Pivot error code data for stacked area
  const errorCodePivot = useMemo(
    () =>
      failures.data
        ? pivotErrorCodeTimeSeries(failures.data.failuresByErrorCodeTimeSeries)
        : { data: [], errorCodes: [] },
    [failures.data]
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Monitoring</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Sandbox Creation metrics</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <TimeRangePicker value={timeRange} onChange={setTimeRange} />
          <button
            onClick={handleRefresh}
            disabled={summary.isFetching || timeSeries.isFetching || failures.isFetching}
            className="p-2 rounded-lg border border-gray-200 dark:border-[#2a2a2a] bg-white dark:bg-[#0a0a0a] text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#1a1a1a] disabled:opacity-50 transition-colors"
            title="Refresh"
          >
            <RefreshCw
              className={`h-4 w-4 ${summary.isFetching || timeSeries.isFetching || failures.isFetching ? 'animate-spin' : ''}`}
            />
          </button>
        </div>
      </div>

      {/* Row 1: Stat Cards */}
      {summary.error && <SectionError message="Failed to load summary statistics" />}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard
          title="Success Rate"
          value={summary.data ? `${summary.data.successRate}` : '--'}
          suffix="%"
          icon={<CheckCircle className="h-3.5 w-3.5" />}
          status={summary.data ? getSuccessRateStatus(summary.data.successRate) : 'neutral'}
          loading={summary.isLoading}
        />
        <StatCard
          title="Total Creates"
          value={summary.data ? formatNumber(summary.data.total) : '--'}
          icon={<Activity className="h-3.5 w-3.5" />}
          loading={summary.isLoading}
        />
        <StatCard
          title="Success"
          value={summary.data ? formatNumber(summary.data.successes) : '--'}
          icon={<CheckCircle className="h-3.5 w-3.5" />}
          status="success"
          loading={summary.isLoading}
        />
        <StatCard
          title="Failed"
          value={summary.data ? formatNumber(summary.data.failures) : '--'}
          icon={<XCircle className="h-3.5 w-3.5" />}
          status={summary.data && summary.data.failures > 0 ? 'danger' : 'neutral'}
          loading={summary.isLoading}
        />
        <StatCard
          title="P50 Latency"
          value={summary.data ? formatNumber(summary.data.p50Ms) : '--'}
          suffix="ms"
          icon={<Timer className="h-3.5 w-3.5" />}
          loading={summary.isLoading}
        />
        <StatCard
          title="P90 Latency"
          value={summary.data ? formatNumber(summary.data.p90Ms) : '--'}
          suffix="ms"
          icon={<TrendingUp className="h-3.5 w-3.5" />}
          loading={summary.isLoading}
        />
      </div>

      {/* Row 2: Time Series Charts */}
      {timeSeries.error && <SectionError message="Failed to load time series data" />}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Success vs Failure Over Time */}
        <Card>
          <CardHeader title="Success vs Failure Over Time" />
          {timeSeries.isLoading ? (
            <ChartSkeleton />
          ) : timeSeries.data && timeSeries.data.successFailureTimeSeries.length > 0 ? (
            <div className="p-4 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={timeSeries.data.successFailureTimeSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                  <XAxis
                    dataKey="timestamp"
                    tickFormatter={formatShortTime}
                    tick={{ fontSize: 11, fill: textColor }}
                    stroke={gridColor}
                    label={{
                      value: 'UTC Time',
                      position: 'insideBottomRight',
                      fontSize: 10,
                      fill: textColor,
                      dy: 10,
                    }}
                  />
                  <YAxis tick={{ fontSize: 11, fill: textColor }} stroke={gridColor} />
                  <Tooltip
                    labelFormatter={formatTimestamp}
                    contentStyle={{
                      backgroundColor: tooltipBg,
                      border: `1px solid ${tooltipBorder}`,
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area
                    type="monotone"
                    dataKey="successes"
                    name="Success"
                    stackId="1"
                    fill="#22c55e"
                    stroke="#16a34a"
                    fillOpacity={0.6}
                  />
                  <Area
                    type="monotone"
                    dataKey="failures"
                    name="Failure"
                    stackId="1"
                    fill="#ef4444"
                    stroke="#dc2626"
                    fillOpacity={0.6}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="p-8 text-center text-sm text-gray-400">
              No data in selected time range
            </div>
          )}
        </Card>

        {/* E2E Latency Over Time */}
        <Card>
          <CardHeader title="E2E Latency Over Time (p50 / p90 / p99)" />
          {timeSeries.isLoading ? (
            <ChartSkeleton />
          ) : timeSeries.data && timeSeries.data.latencyTimeSeries.length > 0 ? (
            <div className="p-4 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={timeSeries.data.latencyTimeSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                  <XAxis
                    dataKey="timestamp"
                    tickFormatter={formatShortTime}
                    tick={{ fontSize: 11, fill: textColor }}
                    stroke={gridColor}
                    label={{
                      value: 'UTC Time',
                      position: 'insideBottomRight',
                      fontSize: 10,
                      fill: textColor,
                      dy: 10,
                    }}
                  />
                  <YAxis tick={{ fontSize: 11, fill: textColor }} stroke={gridColor} />
                  <Tooltip
                    labelFormatter={formatTimestamp}
                    formatter={(value) => [`${Number(value).toLocaleString()} ms`]}
                    contentStyle={{
                      backgroundColor: tooltipBg,
                      border: `1px solid ${tooltipBorder}`,
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line
                    type="monotone"
                    dataKey="p50"
                    name="p50"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="p90"
                    name="p90"
                    stroke="#f97316"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="p99"
                    name="p99"
                    stroke="#8b5cf6"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="p-8 text-center text-sm text-gray-400">
              No data in selected time range
            </div>
          )}
        </Card>
      </div>

      {/* Row 3: Failure Analysis */}
      {failures.error && <SectionError message="Failed to load failure details" />}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Failure Reasons */}
        <Card>
          <CardHeader title="Top Failure Reasons" />
          {failures.isLoading ? (
            <ChartSkeleton />
          ) : failures.data && failures.data.topFailureReasons.length > 0 ? (
            <div className="p-4 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={failures.data.topFailureReasons}
                    dataKey="count"
                    nameKey="reason"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    label={(props) => {
                      const entry = failures.data!.topFailureReasons[props.index];
                      const label = entry?.errorCode || String(props.name ?? '');
                      const pct = typeof props.percent === 'number' ? props.percent : 0;
                      return `${label} (${(pct * 100).toFixed(0)}%)`;
                    }}
                    labelLine={false}
                    fontSize={11}
                  >
                    {failures.data.topFailureReasons.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: tooltipBg,
                      border: `1px solid ${tooltipBorder}`,
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="p-8 text-center text-sm text-gray-400">
              No failures in selected time range
            </div>
          )}
        </Card>

        {/* Failures Over Time by Error Code */}
        <Card>
          <CardHeader title="Failures Over Time by Error Code" />
          {failures.isLoading ? (
            <ChartSkeleton />
          ) : errorCodePivot.data.length > 0 ? (
            <div className="p-4 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={errorCodePivot.data}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                  <XAxis
                    dataKey="timestamp"
                    tickFormatter={formatShortTime}
                    tick={{ fontSize: 11, fill: textColor }}
                    stroke={gridColor}
                    label={{
                      value: 'UTC Time',
                      position: 'insideBottomRight',
                      fontSize: 10,
                      fill: textColor,
                      dy: 10,
                    }}
                  />
                  <YAxis tick={{ fontSize: 11, fill: textColor }} stroke={gridColor} />
                  <Tooltip
                    labelFormatter={formatTimestamp}
                    contentStyle={{
                      backgroundColor: tooltipBg,
                      border: `1px solid ${tooltipBorder}`,
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {errorCodePivot.errorCodes.map((code, i) => (
                    <Area
                      key={code}
                      type="monotone"
                      dataKey={code}
                      name={code}
                      stackId="1"
                      fill={PIE_COLORS[i % PIE_COLORS.length]}
                      stroke={PIE_COLORS[i % PIE_COLORS.length]}
                      fillOpacity={0.6}
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="p-8 text-center text-sm text-gray-400">
              No failures in selected time range
            </div>
          )}
        </Card>
      </div>

      {/* Row 4: Failed Requests Table */}
      <Card>
        <CardHeader title="Failed Requests" />
        {failures.isLoading ? (
          <div className="animate-pulse p-4 space-y-3">
            {Array.from({ length: 5 }, (_, i) => (
              <div key={i} className="h-6 bg-gray-200 dark:bg-gray-700 rounded" />
            ))}
          </div>
        ) : failures.data && failures.data.failedRequests.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-[#111]">
                <tr className="border-b border-gray-100 dark:border-[#2a2a2a] text-left">
                  <th className="px-4 py-3 font-medium text-gray-500 dark:text-gray-400">
                    Timestamp (UTC)
                  </th>
                  <th className="px-4 py-3 font-medium text-gray-500 dark:text-gray-400">
                    Operation
                  </th>
                  <th className="px-4 py-3 font-medium text-gray-500 dark:text-gray-400">
                    Elapsed
                  </th>
                  <th className="px-4 py-3 font-medium text-gray-500 dark:text-gray-400">
                    Error Code
                  </th>
                  <th className="px-4 py-3 font-medium text-gray-500 dark:text-gray-400">Reason</th>
                  <th className="px-4 py-3 font-medium text-gray-500 dark:text-gray-400">
                    Trace ID
                  </th>
                </tr>
              </thead>
              <tbody>
                {failures.data.failedRequests.map((req, i) => (
                  <tr
                    key={i}
                    className="border-b border-gray-50 dark:border-[#1a1a1a] hover:bg-gray-50 dark:hover:bg-[#111]"
                  >
                    <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300 tabular-nums whitespace-nowrap">
                      {formatTimestamp(req.timestamp)}
                    </td>
                    <td className="px-4 py-2.5 text-gray-900 dark:text-white font-mono text-xs">
                      {req.operation ?? '—'}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300 tabular-nums">
                      {req.elapsedMs !== null && req.elapsedMs !== undefined
                        ? `${formatNumber(Math.round(req.elapsedMs))} ms`
                        : '—'}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-400">
                        {req.errorCode ?? 'Unknown'}
                      </span>
                    </td>
                    <td
                      className="px-4 py-2.5 text-gray-600 dark:text-gray-300 max-w-xs truncate"
                      title={req.reason ?? ''}
                    >
                      {req.reason ?? '—'}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {req.traceId ? (
                        <a
                          href={`/logs/traces/${req.traceId}`}
                          className="text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          {req.traceId.slice(0, 12)}…
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center text-sm text-gray-400">
            No failed requests in selected time range
          </div>
        )}
      </Card>
    </div>
  );
}
